import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Settings from '@jwp/ott-theme/assets/icons/Settings.svg?react';
import PlayerAudio from '@jwp/ott-theme/assets/icons/player_Audio.svg?react';
import PlayerFullscreen from '@jwp/ott-theme/assets/icons/player_Full Screen.svg?react';
import PlayerPlaybackSpeed from '@jwp/ott-theme/assets/icons/player_Playback_speed.svg?react';
import PlayerPlay from '@jwp/ott-theme/assets/icons/player_Play_Arrow.svg?react';
import PlayerForward10 from '@jwp/ott-theme/assets/icons/player_Forward 10s.svg?react';
import PlayerReplay10 from '@jwp/ott-theme/assets/icons/player_Replay 10s.svg?react';

import { PauseIcon, SubtitlesIcon, VolumeMutedIcon } from './CustomPlayerIcons';
import CustomPlayerSettingsPanel from './CustomPlayerSettingsPanel';
import CustomPlayerVolumeSlider from './CustomPlayerVolumeSlider';
import type { SettingsPanelType } from './CustomPlayer.types';
import styles from './CustomPlayerControls.module.scss';

type Option = {
  label: string;
  value: string | number;
};

type Props = {
  isPlaying: boolean;
  isMuted: boolean;
  volume: number;
  isFullscreen: boolean;
  activePanel: SettingsPanelType;
  qualityOptions: Option[];
  subtitleOptions: Option[];
  audioTrackOptions: Option[];
  playbackSpeedOptions: Option[];
  selectedQuality: string;
  selectedSubtitle: string;
  selectedAudioTrack: string;
  selectedPlaybackSpeed: number;
  showQuality?: boolean;
  showSubtitles?: boolean;
  showAudioTracks?: boolean;
  showPlaybackSpeed?: boolean;
  showPip?: boolean;
  showUpNext?: boolean;
  onPlayPause: () => void;
  onRewind: () => void;
  onForward: () => void;
  onVolumeToggle: () => void;
  onVolumeChange: (volume: number) => void;
  onPanelToggle: (panel: SettingsPanelType) => void;
  onQualityChange: (value: string) => void;
  onSubtitleChange: (value: string) => void;
  onAudioTrackChange: (value: string) => void;
  onPlaybackSpeedChange: (value: number) => void;
  onFullscreenToggle: () => void;
  onMoreVideos?: () => void;
};

const CustomPlayerControls: React.FC<Props> = ({
  isPlaying,
  isMuted,
  volume,
  isFullscreen,
  activePanel,
  qualityOptions,
  subtitleOptions,
  audioTrackOptions,
  playbackSpeedOptions,
  selectedQuality,
  selectedSubtitle,
  selectedAudioTrack,
  selectedPlaybackSpeed,
  showQuality = true,
  showSubtitles = true,
  showAudioTracks = true,
  showPlaybackSpeed = true,
  onPlayPause,
  onRewind,
  onForward,
  onVolumeToggle: _onVolumeToggle,
  onVolumeChange,
  onPanelToggle,
  onQualityChange,
  onSubtitleChange,
  onAudioTrackChange,
  onPlaybackSpeedChange,
  onFullscreenToggle,
}) => {
  const { t } = useTranslation('player');
  const handlePanelClick = (panel: SettingsPanelType) => {
    onPanelToggle(activePanel === panel ? null : panel);
  };

  const showCaptionsButton = showSubtitles || (showAudioTracks && audioTrackOptions.length > 1);
  const panelProps = {
    qualityOptions,
    subtitleOptions,
    audioTrackOptions,
    playbackSpeedOptions,
    selectedQuality,
    selectedSubtitle,
    selectedAudioTrack,
    selectedPlaybackSpeed,
    onQualityChange,
    onSubtitleChange,
    onAudioTrackChange,
    onPlaybackSpeedChange,
    showQuality,
    showSubtitles,
    showAudioTracks,
    showPlaybackSpeed,
  };

  return (
    <div className={styles.controls}>
      <div className={styles.leftGroup}>
        <div className={styles.pill}>
          <div className={styles.pillChrome} aria-hidden />
          <button type="button" className={styles.controlButton} onClick={onRewind} aria-label={t('rewind')}>
            <PlayerReplay10 />
          </button>
          <button
            type="button"
            className={classNames(styles.controlButton, styles.playButton)}
            onClick={onPlayPause}
            aria-label={isPlaying ? t('pause') : t('play')}
          >
            {isPlaying ? <PauseIcon /> : <PlayerPlay />}
          </button>
          <button type="button" className={styles.controlButton} onClick={onForward} aria-label={t('forward')}>
            <PlayerForward10 />
          </button>
        </div>
      </div>

      <div className={styles.rightGroup}>
        <div className={styles.pill}>
          <div className={styles.pillChrome} aria-hidden />
          <div className={styles.rightItem} data-player-popup>
            <button
              type="button"
              className={classNames(styles.controlButton, { [styles.active]: activePanel === 'settings' })}
              onClick={() => handlePanelClick('settings')}
              aria-label={t('settings')}
            >
              <Settings className={styles.settingsIcon} />
            </button>
            <CustomPlayerSettingsPanel activePanel={activePanel === 'settings' ? 'settings' : null} {...panelProps} />
          </div>

          {showCaptionsButton && (
            <div className={styles.rightItem} data-player-popup>
              <button
                type="button"
                className={classNames(styles.controlButton, { [styles.active]: activePanel === 'subtitles' })}
                onClick={() => handlePanelClick('subtitles')}
                aria-label={t('subtitles')}
              >
                <SubtitlesIcon />
              </button>
              <CustomPlayerSettingsPanel activePanel={activePanel === 'subtitles' ? 'subtitles' : null} {...panelProps} />
            </div>
          )}

          {showPlaybackSpeed && (
            <div className={styles.rightItem} data-player-popup>
              <button
                type="button"
                className={classNames(styles.controlButton, { [styles.active]: activePanel === 'playbackSpeed' })}
                onClick={() => handlePanelClick('playbackSpeed')}
                aria-label={t('playback_speed')}
              >
                <PlayerPlaybackSpeed />
              </button>
              <CustomPlayerSettingsPanel
                activePanel={activePanel === 'playbackSpeed' ? 'playbackSpeed' : null}
                {...panelProps}
              />
            </div>
          )}

          <div className={styles.rightItem} data-player-popup>
            <button
              type="button"
              className={classNames(styles.controlButton, { [styles.active]: activePanel === 'volume' })}
              onClick={() => handlePanelClick('volume')}
              aria-label={t('volume')}
            >
              {isMuted || volume === 0 ? <VolumeMutedIcon /> : <PlayerAudio />}
            </button>
            <CustomPlayerVolumeSlider volume={volume} onChange={onVolumeChange} visible={activePanel === 'volume'} />
          </div>

          <button
            type="button"
            className={styles.controlButton}
            onClick={onFullscreenToggle}
            aria-label={isFullscreen ? t('exit_fullscreen') : t('enter_fullscreen')}
          >
            <PlayerFullscreen />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomPlayerControls;
